/**
 * Profile Page
 * Simplified version - Avatar and Basic Info only
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useProduct } from "@/hooks/useProduct";
import {
  fetchTeacherProfile,
  updateTeacherProfile,
  uploadProfilePhoto,
  clearError,
} from "@/store/slices/teacherProfileSlice";
import {
  selectTeacherProfile,
  selectTeacherProfileLoading,
  selectTeacherProfileError,
  selectUploadProgress,
  selectUserInitials,
} from "@/store/selectors/teacherProfileSelectors";
import {
  createPortalSession,
  fetchUserAccess,
} from "@/store/slices/subscriptionSlice";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useRevenueCatIntegration } from "@/hooks/useRevenueCatIntegration";
import { Camera, Save, X, Loader2, CreditCard, Crown } from "lucide-react";
import { PageLoading } from "@/components/loading/PageLoading";
import { logger } from "@/utils/logger";
import { SEOPresets } from "@/components/SEO";

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const config = useProduct();

  const profile = useAppSelector(selectTeacherProfile);
  const loading = useAppSelector(selectTeacherProfileLoading);
  const error = useAppSelector(selectTeacherProfileError);
  const uploadProgress = useAppSelector(selectUploadProgress);
  const initials = useAppSelector(selectUserInitials);

  const subscriptionState = useAppSelector((state) => state.subscription);
  const { userAccess } = subscriptionState;

  const { user } = useAppSelector((state) => state.auth);

  // Initialize RevenueCat integration - use hook function that validates isActive status
  const {
    hasPremiumAccess: checkPremiumAccess,
    isLoading: subscriptionLoading,
  } = useRevenueCatIntegration();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Check if user has premium subscription
  const hasPremiumAccess = checkPremiumAccess();
  // Use email as customer ID (RevenueCat and Stripe both use email as key)
  const customerId = user?.email;

  // Fetch profile on mount (only once)
  useEffect(() => {
    logger.log("🔄 [Profile] useEffect triggered", {
      hasProfile: !!profile,
      loading,
      error,
    });

    // Clear any previous errors on mount
    if (error && !loading) {
      logger.log("🧹 [Profile] Clearing previous error");
      dispatch(clearError());
    }

    if (!profile && !loading) {
      logger.log("📥 [Profile] Dispatching fetchTeacherProfile");
      dispatch(fetchTeacherProfile(false));
    } else {
      logger.log("⏭️ [Profile] Skipping fetch", {
        reason: profile ? "already has profile" : "currently loading",
      });
    }

    // Fetch subscription data if not loaded
    if (!subscriptionState.accessLoaded) {
      logger.log("📥 [Profile] Dispatching fetchUserAccess");
      dispatch(fetchUserAccess());
    } else {
      logger.log("⏭️ [Profile] Skipping fetchUserAccess", {
        accessLoaded: subscriptionState.accessLoaded,
        hasUserAccess: !!userAccess,
      });
    }
    // Note: fetchRevenueCatStatus is handled by useRevenueCatIntegration hook

    // Debug logs for subscription state
    logger.log("🔍 [Profile] Subscription debug", {
      hasPremiumAccess,
      userAccess,
      customerId,
      userAccessKeys: userAccess ? Object.keys(userAccess) : "undefined",
      rc_app_user_id: userAccess?.rc_app_user_id,
      learner: userAccess?.learner,
      system_user: userAccess?.system_user,
      email: userAccess?.email,
    });
  }, []);

  // Update form when profile loads
  useEffect(() => {
    logger.log("📝 [Profile] Profile data changed:", profile);
    logger.log(
      "📝 [Profile] iso_language type:",
      typeof profile?.iso_language,
      profile?.iso_language
    );
    if (profile) {
      setFormData({
        name: profile.name || "",
        email: profile.email || "",
      });
      setPhotoPreview(profile.photo || null);
    }
  }, [profile]);

  // Track changes (email is readonly, only track name)
  useEffect(() => {
    if (profile) {
      const changed = formData.name !== profile.name;
      setHasChanges(changed);
    }
  }, [formData, profile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: config.i18n.profilePage.invalidFileType,
        description: config.i18n.profilePage.invalidFileType,
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: config.i18n.profilePage.fileTooLarge,
        description: config.i18n.profilePage.fileTooLarge,
        variant: "destructive",
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload photo
    try {
      await dispatch(uploadProfilePhoto({ file })).unwrap();
      toast({
        title: "Success",
        description: config.i18n.profilePage.photoUpdated,
      });
    } catch (error: any) {
      toast({
        title: config.i18n.profilePage.uploadFailed,
        description: error || config.i18n.profilePage.uploadFailed,
        variant: "destructive",
      });
      // Revert preview on error
      setPhotoPreview(profile?.photo || null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profile) {
      toast({
        title: "Error",
        description: config.i18n.profilePage.profileDataNotLoaded,
        variant: "destructive",
      });
      return;
    }

    try {
      // Extract array IDs, filtering empty strings
      const reasonIds =
        profile.reasons && profile.reasons.length > 0
          ? profile.reasons
              .map((r) => (typeof r === "object" && r.id ? r.id : r))
              .filter((id) => id && id !== "")
          : [];

      const langIds =
        profile.program_languages && profile.program_languages.length > 0
          ? profile.program_languages
              .map((p) => (typeof p === "object" && p.id ? p.id : p))
              .filter((id) => id && id !== "")
          : [];

      const skillIds =
        profile.skills && profile.skills.length > 0
          ? profile.skills
              .map((s) => (typeof s === "object" && s.id ? s.id : s))
              .filter((id) => id && id !== "")
          : [];

      // Validar iso_language - backend espera TeacherBaseSchema {id, name} ou null
      let isoLanguage = null;
      if (profile.iso_language && typeof profile.iso_language === "object") {
        // Só envia se tiver id válido
        if (profile.iso_language.id && profile.iso_language.id !== "") {
          isoLanguage = {
            id: profile.iso_language.id,
            name: profile.iso_language.name || "",
          };
        }
      }

      // Validar current_level - backend aceita UUID | TeacherBaseSchema | null
      let currentLevel = null;
      if (profile.current_level) {
        if (
          typeof profile.current_level === "object" &&
          profile.current_level.id &&
          profile.current_level.id !== ""
        ) {
          currentLevel = profile.current_level.id; // Envia como UUID
        } else if (
          typeof profile.current_level === "string" &&
          profile.current_level !== ""
        ) {
          currentLevel = profile.current_level;
        }
      }

      // Backend schema: todos os campos são obrigatórios com valores padrão
      const payload: any = {
        email: profile.email,
        name: formData.name,
        photo: profile.photo || "",
        current_level: currentLevel,
        is_developer: profile.is_developer ?? null,
        dev_level: profile.dev_level || null,
        dev_type: profile.dev_type || null,
        reasons: reasonIds.length > 0 ? reasonIds : [],
        program_languages: langIds.length > 0 ? langIds : [],
        skills: skillIds.length > 0 ? skillIds : [],
        iso_language: isoLanguage, // Envia null ou objeto completo
      };

      logger.log("📤 [Profile] Submitting update with payload:", payload);
      logger.log("📤 [Profile] Payload EXPANDED:");
      logger.log(JSON.stringify(payload, null, 2));
      logger.log("📤 [Profile] reasons:", payload.reasons);
      logger.log("📤 [Profile] program_languages:", payload.program_languages);
      logger.log("📤 [Profile] skills:", payload.skills);
      logger.log("📤 [Profile] iso_language:", payload.iso_language);
      logger.log("📤 [Profile] dev_level:", payload.dev_level);
      logger.log("📤 [Profile] dev_type:", payload.dev_type);
      await dispatch(updateTeacherProfile(payload)).unwrap();
      toast({
        title: config.i18n.profilePage.success,
        description: config.i18n.profilePage.profileUpdated,
      });
      setHasChanges(false);
    } catch (error: any) {
      toast({
        title: config.i18n.profilePage.updateFailed,
        description: error || config.i18n.profilePage.updateFailed,
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name || "",
        email: profile.email || "",
      });
      setPhotoPreview(profile.photo || null);
      setHasChanges(false);
    }
  };

  const handleManageSubscription = async () => {
    let portalUrl = null;

    // First try to use customerId for Stripe portal session
    if (customerId) {
      logger.log(
        "[Profile] Attempting to create Stripe portal session with customerId:",
        customerId
      );
      setPortalLoading(true);
      try {
        const result = await dispatch(
          createPortalSession({
            customerId,
            returnUrl: `${window.location.origin}/dashboard`,
          })
        ).unwrap();
        portalUrl = result.url;
        logger.log(
          "[Profile] Successfully created Stripe portal session:",
          portalUrl
        );
      } catch (error: any) {
        logger.warn(
          "[Profile] Failed to create portal session with customerId:",
          {
            error,
            errorMessage: error?.message,
            errorResponse: error?.response?.data,
            customerId,
          }
        );
        // Fall back to RevenueCat management URL
      } finally {
        setPortalLoading(false);
      }
    } else {
      logger.warn(
        "[Profile] No customerId available, skipping Stripe portal session creation"
      );
    }

    // If customerId approach failed or not available, try RevenueCat management URL
    if (!portalUrl) {
      logger.log("[Profile] Attempting to get RevenueCat management URL");
      try {
        const { Purchases } = await import("@revenuecat/purchases-js");
        const customerInfo =
          await Purchases.getSharedInstance().getCustomerInfo();

        logger.log("[Profile] RevenueCat customer info:", {
          hasManagementURL: !!customerInfo.managementURL,
          managementURL: customerInfo.managementURL,
          customerId: customerInfo.originalAppUserId,
          entitlements: Object.keys(customerInfo.entitlements?.all || {}),
        });

        // Try to create portal session with RevenueCat's customer ID if different
        if (
          customerInfo.originalAppUserId &&
          customerInfo.originalAppUserId !== customerId
        ) {
          logger.log(
            "[Profile] Trying RevenueCat customer ID:",
            customerInfo.originalAppUserId
          );
          try {
            const result = await dispatch(
              createPortalSession({
                customerId: customerInfo.originalAppUserId,
                returnUrl: `${window.location.origin}/dashboard`,
              })
            ).unwrap();
            portalUrl = result.url;
            logger.log(
              "[Profile] Successfully created Stripe portal session with RevenueCat customerId:",
              portalUrl
            );
          } catch (rcError: any) {
            logger.warn("[Profile] Failed with RevenueCat customerId:", {
              rcError,
              rcErrorMessage: rcError?.message,
              rcErrorResponse: rcError?.response?.data,
            });
          }
        }

        // If still no URL, use management URL as last resort
        if (!portalUrl && customerInfo.managementURL) {
          portalUrl = customerInfo.managementURL;
          logger.log("[Profile] Using RevenueCat management URL:", portalUrl);
        } else {
          logger.warn("[Profile] RevenueCat has no management URL");
        }
      } catch (error: any) {
        logger.error(
          "[Profile] Failed to get RevenueCat management URL:",
          error
        );
      }
    }

    if (!portalUrl) {
      logger.error("[Profile] No portal URL available - all methods failed");
      toast({
        title: config.i18n.profilePage.subscriptionUnavailable,
        description: config.i18n.profilePage.subscriptionUnavailableDesc,
        variant: "destructive",
      });
      return;
    }

    logger.log("[Profile] Redirecting to portal:", portalUrl);
    // Redirect to the portal URL
    window.location.href = portalUrl;
  };

  const handleGetPremium = () => {
    navigate("/pricing");
  };

  if ((loading && !profile) || subscriptionLoading) {
    return <PageLoading message={config.i18n.profilePage.loadingMessage} />;
  }

  return (
    <div className="min-h-screen bg-background">
      {SEOPresets.profile}
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Page Title */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-extrabold text-foreground mb-2">
            {config.i18n.profilePage.pageTitle}
          </h1>
          <p className="text-muted-foreground">
            {config.i18n.profilePage.pageSubtitle}
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="mb-6 border-red-500/20 bg-red-500/5">
            <CardContent className="p-4">
              <p className="text-red-500 text-sm">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Profile Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Avatar Section */}
            <Card className="border-2 border-border/50 animate-fade-in">
              <CardHeader>
                <CardTitle>
                  {config.i18n.profilePage.profilePhotoTitle}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4 p-6">
                <div className="relative group">
                  <Avatar className="h-32 w-32 border-4 border-border/50">
                    <AvatarImage
                      src={photoPreview || undefined}
                      alt={formData.name}
                    />
                    <AvatarFallback className="text-4xl bg-gradient-to-br from-primary to-accent text-white">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={handlePhotoClick}
                    className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Camera className="w-8 h-8 text-white" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </div>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="w-full max-w-xs">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      {config.i18n.profilePage.uploadingProgress(
                        uploadProgress
                      )}
                    </p>
                  </div>
                )}

                <p className="text-sm text-muted-foreground text-center">
                  {config.i18n.profilePage.clickToUpload}
                  <br />
                  <span className="text-xs">
                    {config.i18n.profilePage.photoMaxSize}
                  </span>
                </p>
              </CardContent>
            </Card>

            {/* Basic Info Section */}
            <Card className="border-2 border-border/50 animate-fade-in">
              <CardHeader>
                <CardTitle>
                  {config.i18n.profilePage.basicInformationTitle}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {config.i18n.profilePage.fullName}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={config.i18n.profilePage.namePlaceholder}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    {config.i18n.profilePage.emailAddress}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={config.i18n.profilePage.emailPlaceholder}
                    required
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Subscription Management Section */}
            <Card className="border-2 border-border/50 animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {config.i18n.profilePage.subscriptionManagementTitle}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {hasPremiumAccess ? (
                  <>
                    <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
                      <Crown className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium text-foreground">
                        {config.i18n.profilePage.premiumActive}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {config.i18n.profilePage.manageSubscriptionDesc}
                    </p>
                    <Button
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      className="w-full"
                    >
                      {portalLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {config.i18n.profilePage.loading}
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          {config.i18n.profilePage.manageYourSubscription}
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {config.i18n.profilePage.unlockPremiumDesc}
                    </p>
                    <Button
                      onClick={handleGetPremium}
                      variant="default"
                      className="w-full"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      {config.i18n.profilePage.getPremium}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-4 animate-fade-in">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={!hasChanges || loading}
              >
                <X className="w-4 h-4 mr-2" />
                {config.i18n.profilePage.cancel}
              </Button>
              <Button
                type="submit"
                disabled={!hasChanges || loading}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {config.i18n.profilePage.saving}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {config.i18n.profilePage.saveChanges}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
